import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, LocateFixed, MapPin, Search } from "lucide-react";
import api from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { haversine } from "../lib/geo";

// ---- Estilos de mapa ----
// "Map": CARTO (Voyager no tema claro, Dark Matter no escuro). Precisa de uma chave GRATIS
//   (carto.com/basemaps/apikey) colocada em REACT_APP_CARTO_KEY. Sem a chave, usa o mapa do
//   OpenStreetMap como antes, entao nada quebra.
// "Satellite": fotos de satelite do Esri World Imagery (nao precisa de chave).
const CARTO_KEY = (process.env.REACT_APP_CARTO_KEY || "").trim();
const OSM_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const SAT_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const KIND_KEY = "ss_map_kind";
const TILE = 256;

const hiDpi = () => typeof window !== "undefined" && (window.devicePixelRatio || 1) >= 1.5;

export function mapStyleFor(kind, dark) {
  if (kind === "satellite") {
    return { url: SAT_URL, attribution: "Esri, Vantor, Earthstar Geographics, GIS User Community", invert: false, maxZoom: 19 };
  }
  if (CARTO_KEY) {
    const name = dark ? "dark_all" : "voyager";
    return {
      url: `https://basemaps.cartocdn.com/rastertiles/${name}/{z}/{x}/{y}${hiDpi() ? "@2x" : ""}.png?key=${CARTO_KEY}`,
      attribution: "© OpenStreetMap contributors © CARTO",
      invert: false,
      maxZoom: 20,
    };
  }
  return { url: OSM_URL, attribution: "© OpenStreetMap contributors", invert: dark, maxZoom: 19 };
}

function readKind() {
  try {
    return localStorage.getItem(KIND_KEY) === "satellite" ? "satellite" : "map";
  } catch (e) {
    return "map";
  }
}

function clampLat(lat) {
  return Math.max(-85, Math.min(85, lat));
}

function worldX(lng, z) {
  return ((lng + 180) / 360) * TILE * 2 ** z;
}

function worldY(lat, z) {
  const r = (clampLat(lat) * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * TILE * 2 ** z;
}

function metersPerPixel(lat, z) {
  return (156543.03392 * Math.cos((clampLat(lat) * Math.PI) / 180)) / 2 ** z;
}

export function formatDistance(meters) {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

// Mostra onde a pessoa esta (ponto azul) e onde fica a obra (ponto laranja com o
// circulo da cerca). Ajusta o zoom sozinho para caber os dois. Tem botao Map / Satellite.
//   user: { lat, lng, accuracy } | null     site: { lat, lng, radius_m, name } | null
//   label: texto do canto (opcional). Sem ele: a distancia ate a obra, ou o nome da obra se nao ha pessoa.
export default function LocationMap({ user, site, height = 220, showToggle = true, label: labelProp }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(320);
  const [kind, setKind] = useState(readKind);
  const { theme } = useTheme();
  const dark = theme === "dark";
  const style = useMemo(() => mapStyleFor(kind, dark), [kind, dark]);

  const chooseKind = (k) => {
    setKind(k);
    try {
      localStorage.setItem(KIND_KEY, k);
    } catch (e) {
      // sem acesso ao armazenamento: vale so ate fechar
    }
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth || 320);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const view = useMemo(() => {
    const pts = [];
    if (user) pts.push({ lat: user.lat, lng: user.lng, r: 0 });
    if (site) pts.push({ lat: site.lat, lng: site.lng, r: site.radius_m || 200 });
    if (pts.length === 0) return null;

    const centerLat = pts.reduce((a, p) => a + p.lat, 0) / pts.length;
    const centerLng = pts.reduce((a, p) => a + p.lng, 0) / pts.length;

    let z = Math.min(site ? 18 : 17, style.maxZoom);
    for (; z > 2; z -= 1) {
      const cx = worldX(centerLng, z);
      const cy = worldY(centerLat, z);
      const fits = pts.every((p) => {
        const rpx = p.r / metersPerPixel(p.lat, z);
        return (
          Math.abs(worldX(p.lng, z) - cx) + rpx <= width / 2 - 28 &&
          Math.abs(worldY(p.lat, z) - cy) + rpx <= height / 2 - 28
        );
      });
      if (fits) break;
    }

    const cx = worldX(centerLng, z);
    const cy = worldY(centerLat, z);
    const left = cx - width / 2;
    const top = cy - height / 2;
    const n = 2 ** z;

    const tiles = [];
    for (let tx = Math.floor(left / TILE); tx <= Math.floor((cx + width / 2) / TILE); tx += 1) {
      for (let ty = Math.floor(top / TILE); ty <= Math.floor((cy + height / 2) / TILE); ty += 1) {
        if (ty < 0 || ty >= n) continue;
        const wrapped = ((tx % n) + n) % n;
        tiles.push({
          key: `${style.url}|${z}/${tx}/${ty}`,
          src: style.url.replace("{z}", z).replace("{x}", wrapped).replace("{y}", ty),
          x: tx * TILE - left,
          y: ty * TILE - top,
        });
      }
    }

    const at = (p) => ({ x: worldX(p.lng, z) - left, y: worldY(p.lat, z) - top });
    return {
      tiles,
      user: user ? { ...at(user), r: Math.max(9, (user.accuracy || 0) / metersPerPixel(user.lat, z)) } : null,
      site: site ? { ...at(site), r: (site.radius_m || 200) / metersPerPixel(site.lat, z) } : null,
    };
  }, [user, site, width, height, style]);

  const distance = user && site ? haversine(user.lat, user.lng, site.lat, site.lng) : null;
  const label =
    labelProp ||
    (distance !== null
      ? `${formatDistance(distance)} from ${site.name || "the jobsite"}`
      : user
      ? "Your location"
      : (site && site.name) || "Jobsite");

  return (
    <div
      ref={ref}
      className="relative w-full select-none overflow-hidden rounded-lg border border-border bg-muted"
      style={{ height }}
      data-testid="location-map"
      data-map-kind={kind}
    >
      <style>{"@keyframes ssPulse { 0% { transform: scale(1); opacity: .55; } 100% { transform: scale(2.6); opacity: 0; } }"}</style>
      {view && (
        <>
          <div
            className="absolute inset-0"
            style={{ filter: style.invert ? "invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.95)" : "none" }}
          >
            {view.tiles.map((t) => (
              <img
                key={t.key}
                src={t.src}
                alt=""
                draggable={false}
                className="absolute"
                style={{ left: t.x, top: t.y, width: TILE, height: TILE }}
                onError={(e) => {
                  e.currentTarget.style.visibility = "hidden";
                }}
              />
            ))}
          </div>

          {view.site && (
            <>
              <div
                className="absolute rounded-full border-2 border-orange-500 bg-orange-500/15"
                style={{
                  left: view.site.x - view.site.r,
                  top: view.site.y - view.site.r,
                  width: view.site.r * 2,
                  height: view.site.r * 2,
                  boxShadow: "0 0 0 1px rgba(255,255,255,0.6), inset 0 0 0 1px rgba(255,255,255,0.35)",
                }}
              />
              <div
                className="absolute h-4 w-4 rounded-full border-2 border-white bg-orange-500 shadow-md"
                style={{ left: view.site.x - 8, top: view.site.y - 8 }}
              />
            </>
          )}

          {view.user && (
            <>
              <div
                className="absolute rounded-full border border-blue-500/50 bg-blue-500/20"
                style={{ left: view.user.x - view.user.r, top: view.user.y - view.user.r, width: view.user.r * 2, height: view.user.r * 2 }}
              />
              <div
                className="absolute h-4 w-4 rounded-full bg-blue-500"
                style={{ left: view.user.x - 8, top: view.user.y - 8, animation: "ssPulse 1.8s ease-out infinite" }}
              />
              <div
                className="absolute h-4 w-4 rounded-full border-2 border-white bg-blue-500 shadow-md"
                style={{ left: view.user.x - 8, top: view.user.y - 8 }}
              />
            </>
          )}
        </>
      )}

      <div className="absolute left-2 top-2 max-w-[58%] truncate rounded bg-black/75 px-2 py-1 font-mono text-[10px] text-white">
        {label}
      </div>

      {showToggle && (
        <div
          className="absolute right-2 top-2 flex overflow-hidden rounded-md border border-white/25 bg-black/70 text-[11px] font-semibold text-white backdrop-blur"
          role="group"
          aria-label="Map style"
        >
          {[
            ["map", "Map"],
            ["satellite", "Satellite"],
          ].map(([k, text]) => (
            <button
              key={k}
              type="button"
              onClick={() => chooseKind(k)}
              aria-pressed={kind === k}
              className={`h-8 px-3 ${kind === k ? "bg-white text-black" : "text-white/90 active:bg-white/20"}`}
              data-testid={`map-kind-${k}`}
            >
              {text}
            </button>
          ))}
        </div>
      )}

      <div
        className="absolute bottom-1 right-1 max-w-[55%] rounded bg-white/85 px-1 text-right text-[9px] leading-tight text-black"
        data-testid="map-attribution"
      >
        {style.attribution}
      </div>
      <div className="absolute bottom-1 left-2 flex items-center gap-3 rounded bg-black/60 px-2 py-0.5 text-[9px] text-white">
        {user && (
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-blue-500" /> You
          </span>
        )}
        {site && (
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-orange-500" /> Jobsite
          </span>
        )}
      </div>
    </div>
  );
}

// Procura as coordenadas de um endereco (servico gratuito do OpenStreetMap).
async function geocode(query, near) {
  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("limit", "1");
  url.searchParams.set("q", query);
  if (near) {
    // Da preferencia a resultados perto de onde a pessoa esta.
    url.searchParams.set("viewbox", `${near.lng - 2},${near.lat + 2},${near.lng + 2},${near.lat - 2}`);
    url.searchParams.set("bounded", "0");
  }
  const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error("geocode failed");
  const data = await res.json();
  if (!Array.isArray(data) || data.length === 0) return null;
  return { lat: Number(data[0].lat), lng: Number(data[0].lon), label: data[0].display_name };
}

// Quando a obra ainda nao tem local no mapa, o servidor nao tem como saber se a pessoa
// esta perto. Aqui o admin define o local: pelo endereco, ou pela posicao atual.
export function SiteLocationSetup({ site, pos, canEdit, onCandidate, onSaved }) {
  const [finding, setFinding] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [confirmHere, setConfirmHere] = useState(false);
  const [saving, setSaving] = useState(false);

  const query = [site.address, site.state, site.postcode].filter(Boolean).join(", ");

  const choose = (c) => {
    setCandidate(c);
    onCandidate(c);
  };

  const find = async () => {
    setFinding(true);
    try {
      const found = await geocode(query, pos);
      if (!found) {
        toast.error("Could not find this address on the map.");
        return;
      }
      choose(found);
    } catch (e) {
      toast.error("Could not search the address. Check your connection.");
    } finally {
      setFinding(false);
    }
  };

  const save = async (point) => {
    setSaving(true);
    try {
      await api.patch(`/jobsites/${site.id}`, {
        name: site.name,
        address: site.address || "",
        state: site.state || "",
        postcode: site.postcode || "",
        lat: point.lat,
        lng: point.lng,
        radius_m: site.radius_m || 200,
      });
      toast.success("Jobsite location saved");
      setCandidate(null);
      setConfirmHere(false);
      onCandidate(null);
      onSaved({ lat: point.lat, lng: point.lng });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save the jobsite location.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
      <div className="flex items-start gap-2">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div>
          <div className="font-semibold">This jobsite has no map location yet</div>
          <div className="mt-0.5 text-muted-foreground">
            Without it we cannot check if you are near the jobsite, so every clock in counts as inside.
            {!canEdit && " Ask an admin to set it."}
          </div>
        </div>
      </div>

      {canEdit && !candidate && !confirmHere && (
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={find}
            disabled={finding || !query}
            className="flex h-12 items-center justify-center gap-2 rounded-lg border border-border bg-background font-semibold disabled:opacity-50"
            data-testid="find-address"
          >
            {finding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {query ? "Find it from the address" : "This jobsite has no address"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmHere(true)}
            className="flex h-12 items-center justify-center gap-2 rounded-lg border border-border bg-background font-semibold"
            data-testid="use-my-location"
          >
            <LocateFixed className="h-4 w-4" /> Use my current location
          </button>
        </div>
      )}

      {canEdit && candidate && (
        <div className="mt-3 space-y-2">
          <div className="rounded-lg bg-background p-3 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Found:</span> {candidate.label}
            <div className="mt-1">The orange point on the map shows it. Is that the jobsite?</div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setCandidate(null);
                onCandidate(null);
              }}
              disabled={saving}
              className="h-12 flex-1 rounded-lg border border-border bg-background font-semibold"
            >
              No
            </button>
            <button
              type="button"
              onClick={() => save(candidate)}
              disabled={saving}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-primary font-semibold text-black disabled:opacity-60"
              data-testid="save-found-location"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Yes, save it
            </button>
          </div>
        </div>
      )}

      {canEdit && confirmHere && (
        <div className="mt-3 space-y-2">
          <div className="rounded-lg bg-background p-3 text-xs">
            Only do this if you are <span className="font-semibold">standing at the jobsite right now</span>. The
            jobsite will be saved where you are.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmHere(false)}
              disabled={saving}
              className="h-12 flex-1 rounded-lg border border-border bg-background font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => save({ lat: pos.lat, lng: pos.lng })}
              disabled={saving}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-primary font-semibold text-black disabled:opacity-60"
              data-testid="confirm-use-my-location"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Yes, I am here
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
